import { useState, type FormEvent } from 'react';
import { Users, UserPlus, Copy, Check, Trash2, Mail, Pencil, X } from 'lucide-react';
import {
  fullAppAccess,
  type AppAccessCatalog,
  type AppCatalogAppliance,
  type AppSection,
  type AppUser,
  type ApplianceScope,
  type CreateRemoteViewerResponse,
  type ScreenSelection,
  type UserAppAccess,
} from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { Alert, TextField, Toggle } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useSession } from '@/lib/session';
import { useAsync } from '@/lib/useAsync';
import { Card, LoadError, Loading, PortalHeader, timeAgo } from './components';

/**
 * App users (owner): who may use Remote View, Devices and Alarms — in the
 * portal and the Scada.Ink app — on which appliances, and which screens.
 * The cloud account is the only authority; appliance-local permissions do not
 * apply to these people. Contract: packages/shared/src/cloud/access.ts.
 */

const SECTION_LABEL: Record<AppSection, string> = { views: 'Remote View', devices: 'Devices', alarms: 'Alarms' };
const SECTION_HINT: Record<AppSection, string> = {
  views: 'Read-only dashboards, custom pages and reports.',
  devices: 'Connection status of every device on the appliance.',
  alarms: 'Active alarms, history and push notifications in the app.',
};
const SECTION_PACKAGE: Record<AppSection, string> = { views: 'Remote View', devices: 'Remote View or Alerts', alarms: 'Alerts' };

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={() =>
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        })
      }
    >
      {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy link'}
    </Button>
  );
}

function scopeSummary(scope: ApplianceScope, catalog: AppAccessCatalog, section: AppSection): string {
  const covered = catalog.appliances.filter((a) => a.covered[section]);
  if (scope === 'all') return covered.length === 1 ? covered[0].name : `All appliances (${covered.length})`;
  const names = covered.filter((a) => scope.includes(a.id)).map((a) => a.name);
  return names.length === 0 ? 'No appliances' : names.length <= 2 ? names.join(', ') : `${names.length} appliances`;
}

function AccessSummary({ access, catalog }: { access: UserAppAccess; catalog: AppAccessCatalog }) {
  const available = (s: AppSection) => (s === 'alarms' ? catalog.packages.alarms : s === 'views' ? catalog.packages.views : catalog.packages.views || catalog.packages.alarms);
  return (
    <div className="flex flex-wrap gap-1.5">
      {(Object.keys(SECTION_LABEL) as AppSection[]).map((s) => {
        const on = access[s].enabled && available(s);
        return (
          <span
            key={s}
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${on ? 'bg-brand-primary/10 text-brand-primary' : 'bg-ink-muted text-text-muted line-through'}`}
            title={on ? scopeSummary(access[s].appliances, catalog, s) : available(s) ? 'Off for this person' : `Needs ${SECTION_PACKAGE[s]}`}
          >
            {SECTION_LABEL[s]}
            {on && access[s].appliances !== 'all' ? ` · ${scopeSummary(access[s].appliances, catalog, s)}` : ''}
          </span>
        );
      })}
    </div>
  );
}

// ── The editor ─────────────────────────────────────────────────────────────

function ScreenPicker({
  appliance,
  value,
  onChange,
}: {
  appliance: AppCatalogAppliance;
  value: 'all' | ScreenSelection;
  onChange: (next: 'all' | ScreenSelection) => void;
}) {
  const groups: Array<{ key: keyof ScreenSelection; label: string }> = [
    { key: 'dashboards', label: 'Dashboards' },
    { key: 'customPages', label: 'Custom pages' },
    { key: 'reports', label: 'Reports' },
  ];
  const sel = value === 'all' ? null : value;
  const toggle = (key: keyof ScreenSelection, id: number) => {
    const base = sel ?? { dashboards: [], customPages: [], reports: [] };
    const list = base[key].includes(id) ? base[key].filter((x) => x !== id) : [...base[key], id];
    onChange({ ...base, [key]: list });
  };
  return (
    <div className="ml-6 mt-2 mb-3 pl-3 border-l border-glass-border space-y-2">
      <div className="flex gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input type="radio" checked={value === 'all'} onChange={() => onChange('all')} /> All screens
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="radio" checked={value !== 'all'} onChange={() => onChange({ dashboards: [], customPages: [], reports: [] })} /> Only these
        </label>
      </div>
      {sel &&
        (appliance.screensAsOf === null ? (
          <p className="text-xs text-text-muted">
            The screen list arrives from the appliance once it reports to Scada.Ink Cloud. Until then this person sees no screens here.
          </p>
        ) : (
          <div className="grid sm:grid-cols-3 gap-3">
            {groups.map((g) => (
              <div key={g.key}>
                <div className="text-xs font-semibold text-text-secondary mb-1">{g.label}</div>
                {appliance.screens[g.key].length === 0 ? (
                  <div className="text-xs text-text-muted">None</div>
                ) : (
                  appliance.screens[g.key].map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm py-0.5">
                      <input type="checkbox" checked={sel[g.key].includes(s.id)} onChange={() => toggle(g.key, s.id)} />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))
                )}
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}

export function AccessEditor({ value, onChange, catalog }: { value: UserAppAccess; onChange: (next: UserAppAccess) => void; catalog: AppAccessCatalog }) {
  const sections = Object.keys(SECTION_LABEL) as AppSection[];
  const packageOn = (s: AppSection) => (s === 'alarms' ? catalog.packages.alarms : s === 'views' ? catalog.packages.views : catalog.packages.views || catalog.packages.alarms);

  const setSection = (s: AppSection, patch: Partial<UserAppAccess[AppSection]>) => onChange({ ...value, [s]: { ...value[s], ...patch } });

  return (
    <div className="space-y-5">
      {sections.map((s) => {
        const covered = catalog.appliances.filter((a) => a.covered[s]);
        const scope = value[s].appliances;
        const chosen = (id: string) => scope === 'all' || scope.includes(id);
        const pick = (id: string) => {
          const list = scope === 'all' ? covered.map((a) => a.id) : scope;
          setSection(s, { appliances: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] });
        };
        return (
          <div key={s}>
            <Toggle
              checked={value[s].enabled && packageOn(s)}
              disabled={!packageOn(s)}
              onChange={(enabled) => setSection(s, { enabled })}
              label={SECTION_LABEL[s]}
              description={packageOn(s) ? SECTION_HINT[s] : `Needs a ${SECTION_PACKAGE[s]} subscription.`}
            />
            {value[s].enabled && packageOn(s) && (
              <div className="ml-[52px] mt-2 space-y-1">
                <div className="flex gap-4 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" checked={scope === 'all'} onChange={() => setSection(s, { appliances: 'all' })} /> All appliances, including new ones
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" checked={scope !== 'all'} onChange={() => setSection(s, { appliances: covered.map((a) => a.id) })} /> Only these
                  </label>
                </div>
                {covered.length === 0 && <p className="text-xs text-text-muted">No appliance has {SECTION_LABEL[s]} yet.</p>}
                {covered.map((a) => (
                  <div key={a.id}>
                    {scope !== 'all' && (
                      <label className="flex items-center gap-2 text-sm py-0.5">
                        <input type="checkbox" checked={chosen(a.id)} onChange={() => pick(a.id)} /> {a.name}
                        {a.site && <span className="text-xs text-text-muted">{a.site}</span>}
                      </label>
                    )}
                    {s === 'views' && chosen(a.id) && (
                      <>
                        {scope === 'all' && <div className="text-sm font-medium mt-1">{a.name}</div>}
                        <ScreenPicker
                          appliance={a}
                          value={value.views.screens[a.id] ?? 'all'}
                          onChange={(next) => {
                            const screens = { ...value.views.screens };
                            if (next === 'all') delete screens[a.id];
                            else screens[a.id] = next;
                            onChange({ ...value, views: { ...value.views, screens } });
                          }}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── The page ───────────────────────────────────────────────────────────────

function UserRow({ user, catalog, onChanged }: { user: AppUser; catalog: AppAccessCatalog; onChanged: (notice?: Notice) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<UserAppAccess>(user.access);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await cloudApi.put<AppUser>(`/api/portal/app-users/${encodeURIComponent(user.id)}/access`, draft);
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    try {
      const res = await cloudApi.post<CreateRemoteViewerResponse>(`/api/portal/remote/viewers/${encodeURIComponent(user.id)}/resend`);
      onChanged({ kind: 'success', text: res.emailed ? `New invitation emailed to ${user.email}. Link:` : `New invitation link for ${user.email}:`, link: res.inviteUrl });
    } catch (err) {
      onChanged({ kind: 'error', text: errorMessage(err) });
    }
  }

  async function remove() {
    if (!window.confirm(user.kind === 'user' ? `Remove ${user.email}? They are signed out everywhere at once.` : `Cancel the invitation for ${user.email}?`)) return;
    try {
      await cloudApi.del(`/api/portal/remote/viewers/${encodeURIComponent(user.id)}`);
      onChanged();
    } catch (err) {
      onChanged({ kind: 'error', text: errorMessage(err) });
    }
  }

  const status =
    user.kind === 'user' ? (user.lastLoginAt ? `signed in ${timeAgo(user.lastLoginAt)}` : 'not signed in yet') : user.status === 'invited' ? 'invited' : 'invitation expired';

  return (
    <li className="py-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">
            {user.name || user.email} <span className="text-xs font-normal text-text-muted capitalize">· {user.role}</span>
          </div>
          <div className="text-xs text-text-muted truncate">
            {user.email} · {status}
          </div>
        </div>
        <AccessSummary access={user.access} catalog={catalog} />
        <div className="flex items-center">
          {user.editable && (
            <button type="button" onClick={() => { setDraft(user.access); setEditing(!editing); }} className="p-1.5 text-text-muted hover:text-brand-primary" aria-label={`Edit access for ${user.email}`} title="Edit access">
              {editing ? <X size={14} /> : <Pencil size={14} />}
            </button>
          )}
          {user.kind === 'invite' && (
            <button type="button" onClick={() => void resend()} className="p-1.5 text-text-muted hover:text-brand-primary" aria-label={`Resend invitation to ${user.email}`} title="Resend invitation">
              <Mail size={14} />
            </button>
          )}
          {user.role === 'viewer' && (
            <button type="button" onClick={() => void remove()} className="p-1.5 text-text-muted hover:text-status-fault" aria-label={`Remove ${user.email}`} title="Remove">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
      {editing && (
        <div className="mt-3 p-4 rounded-lg border border-glass-border bg-white/60">
          <AccessEditor value={draft} onChange={setDraft} catalog={catalog} />
          {error && (
            <div className="mt-3">
              <Alert kind="error">{error}</Alert>
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save access'}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

type Notice = { kind: 'success' | 'error'; text: string; link?: string };

function InviteForm({ catalog, onInvited }: { catalog: AppAccessCatalog; onInvited: (notice: Notice) => void }) {
  const [form, setForm] = useState({ name: '', email: '' });
  const [access, setAccess] = useState<UserAppAccess>(fullAppAccess());
  const [showAccess, setShowAccess] = useState(false);
  const [busy, setBusy] = useState(false);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await cloudApi.post<CreateRemoteViewerResponse>('/api/portal/app-users/invites', { ...form, access });
      setForm({ name: '', email: '' });
      setAccess(fullAppAccess());
      setShowAccess(false);
      onInvited({
        kind: 'success',
        text: res.emailed ? `Invitation emailed to ${res.viewer.email}. You can also send them this link:` : `Send ${res.viewer.email} this invitation link (it works once and expires):`,
        link: res.inviteUrl,
      });
    } catch (err) {
      onInvited({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={invite} className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <TextField label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <TextField label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <button type="button" className="text-xs font-semibold text-brand-primary" onClick={() => setShowAccess(!showAccess)}>
        {showAccess ? 'Hide access' : 'Choose what they can use (default: everything)'}
      </button>
      {showAccess && (
        <div className="p-4 rounded-lg border border-glass-border bg-white/60">
          <AccessEditor value={access} onChange={setAccess} catalog={catalog} />
        </div>
      )}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={busy}>
          <UserPlus size={13} /> {busy ? 'Inviting…' : 'Invite'}
        </Button>
      </div>
    </form>
  );
}

export function AppUsersPage() {
  usePageTitle('App users');
  const { session } = useSession();
  const isOwner = session?.user.role === 'owner';
  const data = useAsync(async () => {
    if (!isOwner) return null;
    const [users, catalog] = await Promise.all([
      cloudApi.get<AppUser[]>('/api/portal/app-users'),
      cloudApi.get<AppAccessCatalog>('/api/portal/app-users/catalog'),
    ]);
    return { users, catalog };
  }, [isOwner]);
  const [notice, setNotice] = useState<Notice | null>(null);

  const changed = (n?: Notice) => {
    setNotice(n ?? null);
    void data.reload();
  };

  return (
    <>
      <PortalHeader
        title="App users"
        description="Who can use Remote View, Devices and Alarms, in this portal and the Scada.Ink app, on which appliances, and which screens. Remote access is managed here, not on the appliance: its own users and permissions do not apply."
      />
      {!isOwner ? (
        <Card>
          <p className="text-sm text-text-muted">Only the account owner can manage app users.</p>
        </Card>
      ) : data.loading && !data.data ? (
        <Loading />
      ) : data.error || !data.data ? (
        <LoadError message={data.error ?? 'Could not load app users.'} onRetry={data.reload} />
      ) : (
        <div className="space-y-6">
          {!data.data.catalog.packages.views && !data.data.catalog.packages.alarms && (
            <Alert kind="info">App access needs a Remote View or Alerts subscription. Add one under Plans &amp; Billing.</Alert>
          )}
          {notice && (
            <Alert kind={notice.kind}>
              {notice.text}
              {notice.link && (
                <span className="mt-2 flex flex-wrap items-center gap-2">
                  <code className="px-2 py-1 rounded bg-white border border-glass-border text-xs break-all text-text-primary">{notice.link}</code>
                  <CopyButton text={notice.link} />
                </span>
              )}
            </Alert>
          )}
          <Card
            title={
              <span className="inline-flex items-center gap-2">
                <Users size={15} /> People
              </span>
            }
            actions={
              data.data.catalog.packages.views && (
                <span className="text-xs text-text-secondary">
                  Remote View seats: {data.data.catalog.viewerSeats.used} of {data.data.catalog.viewerSeats.limit === null ? 'unlimited' : data.data.catalog.viewerSeats.limit}
                </span>
              )
            }
          >
            <p className="text-sm text-text-secondary -mt-1 mb-2">
              Invited people sign in to scada.ink and the app, and can only see what you allow here, read-only. Only people with Remote View use a seat. Owners and team
              members never do.
            </p>
            <ul className="divide-y divide-glass-border">
              {data.data.users.map((u) => (
                <UserRow key={u.id} user={u} catalog={data.data!.catalog} onChanged={changed} />
              ))}
            </ul>
          </Card>
          <Card
            title={
              <span className="inline-flex items-center gap-2">
                <UserPlus size={15} /> Invite someone
              </span>
            }
          >
            <InviteForm catalog={data.data.catalog} onInvited={changed} />
          </Card>
          <p className="text-xs text-text-muted">
            Limiting screens hides those screens from a person. It does not make the tag values behind them secret: anyone with Remote View on an appliance can read its
            tag data. Invitation links expire if they are not accepted; resend one for a fresh link.
          </p>
        </div>
      )}
    </>
  );
}
